import { useState, useRef, useEffect } from "react";
import { Facebook, Twitter, Linkedin, Instagram, Youtube, Link } from "lucide-react";

// Text Block
const TextBlock = ({ block, onUpdate, isEditing }) => {
  const [isEditingText, setIsEditingText] = useState(false);
  const editorRef = useRef(null);

  const handleBlur = () => {
    if (editorRef.current) {
      onUpdate({ content: editorRef.current.innerHTML });
    }
    setIsEditingText(false);
  };

  return (
    <div
      ref={editorRef}
      contentEditable={isEditing}
      suppressContentEditableWarning
      onFocus={() => setIsEditingText(true)}
      onBlur={handleBlur}
      className={`
        outline-none min-h-[1em] 
        ${isEditing ? "cursor-text" : "cursor-pointer"}
      `}
      style={{
        fontSize: block.fontSize || "16px",
        color: block.textColor || "#1f2937",
        textAlign: block.textAlign || "left",
        lineHeight: block.lineHeight || "1.6",
        fontWeight: block.fontWeight || "normal",
        padding: block.padding || "0",
      }}
      dangerouslySetInnerHTML={{ __html: block.content || "Click to edit text..." }}
    />
  );
};

// Image Block
const ImageBlock = ({ block, onUpdate, isEditing }) => {
  return (
    <div
      style={{
        textAlign: block.alignment || "center",
        padding: block.padding || "0",
      }}
    >
      {block.src ? (
        <img
          src={block.src}
          alt={block.alt || ""}
          style={{
            maxWidth: block.width || "100%",
            height: "auto",
            borderRadius: block.borderRadius || "0",
          }}
        />
      ) : (
        <div className="border-2 border-dashed border-border/50 rounded-lg p-8 text-center text-muted-foreground bg-muted/30">
          <p className="text-sm">Click to add image URL</p>
        </div>
      )}
    </div>
  );
};

// Button Block
const ButtonBlock = ({ block, onUpdate, isEditing }) => {
  return (
    <div
      style={{
        textAlign: block.alignment || "center",
        padding: block.padding || "10px 0",
      }}
    >
      <a
        href={block.href || "#"}
        style={{
          display: "inline-block",
          backgroundColor: block.backgroundColor || "#1CBCF8",
          color: block.textColor || "#ffffff",
          padding: block.buttonPadding || "12px 24px",
          borderRadius: block.borderRadius || "6px",
          textDecoration: "none",
          fontWeight: block.fontWeight || "600",
          fontSize: block.fontSize || "14px",
        }}
      >
        {block.text || "Click Here"}
      </a>
    </div>
  );
};

// Divider Block
const DividerBlock = ({ block }) => {
  return (
    <div
      style={{
        padding: block.padding || "10px 0",
      }}
    >
      <hr
        style={{
          border: "none",
          borderTop: `${block.thickness || "1px"} ${block.style || "solid"} ${block.color || "#e5e7eb"}`,
          margin: 0,
        }}
      />
    </div>
  );
};

// Spacer Block
const SpacerBlock = ({ block }) => {
  return (
    <div
      style={{
        height: block.height || "20px",
      }}
    />
  );
};

// Social Block
const SocialBlock = ({ block }) => {
  const socialIcons = {
    facebook: Facebook,
    twitter: Twitter,
    linkedin: Linkedin,
    instagram: Instagram,
    youtube: Youtube,
    website: Link,
  };

  const enabledSocials = block.socials || [
    { type: "facebook", url: "#" },
    { type: "twitter", url: "#" },
    { type: "linkedin", url: "#" },
  ];

  return (
    <div
      style={{
        textAlign: block.alignment || "center",
        padding: block.padding || "10px 0",
      }}
    >
      <div style={{ display: "inline-flex", gap: block.spacing || "12px" }}>
        {enabledSocials.map((social, index) => {
          const Icon = socialIcons[social.type] || Link;
          return (
            <a
              key={index}
              href={social.url || "#"}
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: block.iconSize || "32px",
                height: block.iconSize || "32px",
                borderRadius: block.iconStyle === "circle" ? "50%" : "4px",
                backgroundColor: block.iconBackground || "#1CBCF8",
                color: block.iconColor || "#ffffff",
                textDecoration: "none",
              }}
            >
              <Icon size={parseInt(block.iconSize || "32") * 0.5} />
            </a>
          );
        })}
      </div>
    </div>
  );
};

// Columns Block
const ColumnsBlock = ({ block, onUpdate, isEditing }) => {
  const columnCount = block.columns || 2;
  const columnContent = block.columnContent || Array(columnCount).fill({ content: "" });

  return (
    <div
      style={{
        display: "flex",
        gap: block.gap || "20px",
        padding: block.padding || "0",
      }}
    >
      {Array(columnCount)
        .fill(0)
        .map((_, index) => (
          <div
            key={index}
            style={{
              flex: 1,
              padding: "10px",
              backgroundColor: block.columnBackground || "transparent",
              borderRadius: "4px",
              minHeight: "60px",
            }}
            contentEditable={isEditing}
            suppressContentEditableWarning
            dangerouslySetInnerHTML={{
              __html: columnContent[index]?.content || `Column ${index + 1}`,
            }}
          />
        ))}
    </div>
  );
};

// List Block
const ListBlock = ({ block, onUpdate, isEditing }) => {
  const ListTag = block.listType === "numbered" ? "ol" : "ul";
  const items = block.items || ["Item 1", "Item 2", "Item 3"];

  return (
    <ListTag
      style={{
        margin: 0,
        padding: block.padding || "0 0 0 20px",
        color: block.textColor || "#1f2937",
        fontSize: block.fontSize || "16px",
        lineHeight: block.lineHeight || "1.8",
      }}
    >
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ListTag>
  );
};

// HTML Block
const HtmlBlock = ({ block }) => {
  return (
    <div
      dangerouslySetInnerHTML={{ __html: block.html || "<p>Custom HTML</p>" }}
      style={{ padding: block.padding || "0" }}
    />
  );
};

// Main renderer
const BlockRenderer = ({ block, onUpdate, isEditing }) => {
  const renderers = {
    text: TextBlock,
    image: ImageBlock,
    button: ButtonBlock,
    divider: DividerBlock,
    spacer: SpacerBlock,
    social: SocialBlock,
    columns: ColumnsBlock,
    list: ListBlock,
    html: HtmlBlock,
  };

  const Renderer = renderers[block.type];

  if (!Renderer) {
    return (
      <div className="p-4 bg-red-100 text-red-600 rounded">
        Unknown block type: {block.type}
      </div>
    );
  }

  return <Renderer block={block} onUpdate={onUpdate} isEditing={isEditing} />;
};

export default BlockRenderer;
